import { useEffect, useSyncExternalStore } from "react";
import { useUploadQueueStore } from "@/hooks/useUploadQueueStore";
import {
  checkNewIssues,
  clear,
  dismiss,
  getNudge,
  subscribe,
  syncUpload,
  type ChatNudge,
} from "./chatNudgeStore";

export interface UseChatNudgesResult {
  /** The single nudge to render, or null. */
  nudge: ChatNudge | null;
  /** Dismiss the current nudge — its type stays quiet for the session. */
  dismiss: () => void;
  /** Clear the current nudge without silencing its type (e.g. after acting on it). */
  clear: () => void;
}

/**
 * React binding for the proactive chat nudges. All the guardrails and the
 * event wiring live in `chatNudgeStore`; this hook only tells it when an upload
 * batch finished and returns whatever it decides to show.
 */
export function useChatNudges(
  buildingId: string,
  /** Active chat project scope — narrows the new-issues count when set. */
  projectId?: string | null
): UseChatNudgesResult {
  const uploadStore = useUploadQueueStore();
  const nudge = useSyncExternalStore(subscribe, getNudge);

  // Reading these inside the hook keeps the calling `observer` component
  // subscribed to the MST queue.
  const total = uploadStore.totalCount;
  const done = uploadStore.completedCount;
  const isComplete = uploadStore.isComplete;

  useEffect(() => {
    syncUpload({ buildingId, total, done, isComplete });
  }, [buildingId, total, done, isComplete]);

  useEffect(() => {
    checkNewIssues(buildingId, projectId);
  }, [buildingId, projectId]);

  return { nudge, dismiss, clear };
}
